/**
 * Conversation history storage as the dashboard shows it (GET /v1/admin/dashboard `historyStorage`, also on /readyz).
 * Production keeps every saved chat in one document; past its ceiling, a save that would grow it is refused with the
 * limit named. These warnings put that in front of an admin before people notice their chats stopped syncing.
 */
export interface HistoryStorageState {
  backend: 'legacy' | 'keyed';
  /** Whether the service holds a successfully read copy of the document (it is read on first use). */
  loaded?: boolean;
  readFailures?: number;
  documentBytes?: number;
  maxDocumentBytes?: number;
  largestOwnerBytes?: number;
  owners?: number;
  refusedAtCeiling?: number;
}

export interface HistoryStorageWarning {
  level: 'alert' | 'warn';
  title: string;
  text: string;
}

const mib = (bytes: number): string => `${(bytes / 1024 / 1024).toFixed(1)} MiB`;
const plural = (n: number, word: string): string => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

/** The warning to show for `state`, or null when there is nothing an admin needs to act on. */
export function historyStorageWarning(state: HistoryStorageState | undefined): HistoryStorageWarning | null {
  if (!state || state.backend !== 'legacy') return null;
  const max = state.maxDocumentBytes;
  if (!state.loaded && (state.readFailures ?? 0) > 0) {
    return {
      level: 'alert',
      title: 'History storage could not be read',
      text: `The service could not read the saved-chat document (${plural(state.readFailures ?? 0, 'failed read')}). Opening a saved chat answers "unavailable" and nothing is written over it; it tries again on the next request.`,
    };
  }
  if (!max || state.documentBytes === undefined) return null;
  const largest = state.largestOwnerBytes !== undefined ? ` The largest single person-and-app list holds ${mib(state.largestOwnerBytes)}.` : '';
  if ((state.refusedAtCeiling ?? 0) > 0) {
    return {
      level: 'alert',
      title: 'History storage is full',
      text: `${plural(state.refusedAtCeiling ?? 0, 'chat save')} ${state.refusedAtCeiling === 1 ? 'was' : 'were'} refused because the saved-chat document reached its limit of ${mib(max)} (now ${mib(state.documentBytes)}).${largest} Nothing stored was lost. Move history to the database (GYRO_DB_URL or GYRO_SQLITE_PATH) or raise GYRO_HISTORY_MAX_DOCUMENT_MIB.`,
    };
  }
  if (state.documentBytes >= max * 0.8) {
    return {
      level: 'warn',
      title: 'History storage is nearly full',
      text: `The saved-chat document is ${mib(state.documentBytes)} of its ${mib(max)} limit, across ${plural(state.owners ?? 0, 'person-and-app list')}.${largest} At the limit, saves that would grow it are refused, naming the limit.`,
    };
  }
  return null;
}
